/**
 * src/lib/quizzes.ts
 * Utilitário para carregar e parsear os quizzes de personalidade a partir de arquivos .md.
 */

export interface Quiz {
  slug: string;
  title: string;
  tldr: string;
  thumbnailUrl: string;
  questions: QuizQuestion[];
  results: Record<string, QuizResult>;
}

export interface QuizQuestion {
  id: number;
  question: string;
  options: QuizOption[];
}

export interface QuizOption {
  text: string;
  points: Record<string, number>;
}

export interface QuizResult {
  id: string;
  title: string;
  description: string;
  shortName: string;
  persona: string;
}

function parseScalar(value: string): any {
  const trimmed = value.trim();
  if (trimmed === 'true') return true;
  if (trimmed === 'false') return false;
  if (/^-?\d+(\.\d+)?$/.test(trimmed)) return Number(trimmed);
  return trimmed.replace(/^['"]([\s\S]*?)['"]$/, '$1');
}

export function parseYaml(yaml: string): any {
  const lines = yaml.split('\n')
    .map(line => line.replace(/\t/g, '  '))
    .filter(line => line.trim() !== '' && !line.trim().startsWith('#'));

  const root: any = {};
  const stack: { indent: number; value: any; key?: string; isArray?: boolean }[] = [
    { indent: -1, value: root }
  ];

  for (let i = 0; i < lines.length; i++) {
    const rawLine = lines[i];
    const indent = rawLine.match(/^\s*/)?.[0].length ?? 0;
    const line = rawLine.trim();

    while (stack.length > 1 && stack[stack.length - 1].indent > indent) {
      stack.pop();
    }

    const parent = stack[stack.length - 1];

    if (line.startsWith('-')) {
      const listContent = line.slice(1).trim();
      let currentArray: any[];
      if (parent.isArray) {
        currentArray = parent.value;
      } else if (parent.key && parent.value[parent.key] !== undefined && Array.isArray(parent.value[parent.key])) {
        currentArray = parent.value[parent.key];
      } else {
        currentArray = [];
        if (parent.key) {
          parent.value[parent.key] = currentArray;
        }
      }

      if (listContent.includes(':')) {
        const colonIdx = listContent.indexOf(':');
        const k = listContent.slice(0, colonIdx).trim();
        const v = parseScalar(listContent.slice(colonIdx + 1).trim());

        const itemObj: any = {};
        if (k) {
          itemObj[k] = v;
        }
        currentArray.push(itemObj);
        stack.push({ indent: indent + 2, value: itemObj, isArray: false });
      } else if (listContent !== '') {
        currentArray.push(parseScalar(listContent));
      } else {
        const itemObj: any = {};
        currentArray.push(itemObj);
        stack.push({ indent: indent + 2, value: itemObj, isArray: false });
      }
    } else {
      const colonIdx = line.indexOf(':');
      if (colonIdx === -1) continue;

      const key = line.slice(0, colonIdx).trim();
      const rawValue = line.slice(colonIdx + 1).trim();

      if (rawValue !== '') {
        let val: any;
        if (rawValue.startsWith('[') && rawValue.endsWith(']')) {
          val = rawValue
            .slice(1, -1)
            .split(',')
            .map(s => s.trim().replace(/^['"]|['"]$/g, ''))
            .filter(Boolean);
        } else {
          val = parseScalar(rawValue);
        }
        parent.value[key] = val;
      } else {
        let isNextArray = false;
        let nextLineIndent = indent + 2;
        if (i + 1 < lines.length) {
          const nextRaw = lines[i + 1];
          const nextLineTrim = nextRaw.trim();
          nextLineIndent = nextRaw.match(/^\s*/)?.[0].length ?? 0;
          if (nextLineTrim.startsWith('-')) {
            isNextArray = true;
          }
        }

        if (isNextArray) {
          parent.value[key] = [];
          stack.push({ indent: nextLineIndent, value: parent.value[key], key, isArray: true });
        } else {
          parent.value[key] = {};
          stack.push({ indent: nextLineIndent, value: parent.value[key], key, isArray: false });
        }
      }
    }
  }

  return root;
}

function parseFrontmatter(raw: string): { meta: any; body: string } {
  const match = raw.match(/^---\s*\n([\s\S]*?)\n---\s*\n?([\s\S]*)$/);
  if (!match) return { meta: {}, body: raw };
  const yamlStr = match[1];
  const body = match[2];
  try {
    const meta = parseYaml(yamlStr);
    return { meta, body };
  } catch (err) {
    console.error('Error parsing quiz YAML frontmatter:', err);
    return { meta: {}, body };
  }
}

type MarkdownContext = {
  (key: string): string;
  keys: () => string[];
};

const emptyMarkdownContext = Object.assign((() => '') as unknown as MarkdownContext, {
  keys: () => [],
});

let ctx: MarkdownContext = emptyMarkdownContext;

try {
  ctx = (require as any).context('../content/quizzes', false, /\.md$/);
} catch {
  ctx = emptyMarkdownContext;
}

export function getAllQuizzes(): Quiz[] {
  return (ctx.keys() as string[]).map((key: string) => {
    const raw: string = ctx(key);
    const slug = key.replace(/^\.\//, '').replace(/\.md$/, '');
    const { meta } = parseFrontmatter(raw);

    return {
      slug,
      title: meta.title ?? '',
      tldr: meta.tldr ?? '',
      thumbnailUrl: meta.thumbnailUrl ?? '',
      questions: Array.isArray(meta.questions) ? meta.questions : [],
      results: meta.results ?? {},
    } as Quiz;
  });
}

export function getQuizBySlug(slug: string): Quiz | undefined {
  return getAllQuizzes().find((q) => q.slug === slug);
}

export function __setContextForTest(mockCtx: any) {
  ctx = mockCtx;
}
